import 'whatwg-fetch';

import PropTypes from 'prop-types';
import React, {
  Component,
  Fragment,
  useEffect,
  useState,
} from 'react';
import Modal from 'react-modal';

import APIClient, {
  iterUnpack,
  defaultProductImageUrl,
  defaultLogoImageUrl,
  defaultUserAvatarUrl,
} from '../../services/APIClient';
import BrandViewProductsContent from './BrandViewProductsContent';
import ModalGallery from '../ModalGallery';
import ReadMoreText from '../ReadMoreText';
import TagsDisplay from '../brand_edit_details/ProductDetailTagsDisplay';
import Alert from '../Alert';
import ImageOverPlaceholder, { ASPECT_RATIOS } from '../ImageOverPlaceholder';
import SegmentClient from '../../services/SegmentClient';
import { ANALYTICS_NAME } from './Constants';
import { ProductResource, UserResource } from '../../services/RESTClient';
import { architizerHistoryListener } from '../../services/utils';
import GA4Client from '../../services/GA4Client';

const segment = new SegmentClient();
const ga4 = new GA4Client();
const userResource = new UserResource();

const IS_BRAND_ADMIN = JSON.parse(document.getElementById('root').getAttribute('data-is-brand-admin'));
const BRAND_SLUG = document.getElementById('root').getAttribute('data-brand-slug');

Modal.setAppElement('#root');

const ProductCard = ({ brandSlug, product }) => (
  <div className="column column-block">
    <ImageOverPlaceholder
      aspectRatio={ASPECT_RATIOS.STANDARD}
      image={{ url: product.heroUrl, width: 325, height: 245 }}
      placeholder={defaultProductImageUrl}
      className="mb-xxxs"
      href={`/brands/${brandSlug}/products/${product.slug}/`}
    />
    <a
      href={`/brands/${brandSlug}/products/${product.slug}/`}
      target="_blank"
      rel="noreferrer"
      className="black"
    >
      {product.title}
    </a>
  </div>
);

ProductCard.propTypes = {
  brandSlug: PropTypes.string.isRequired,
  product: PropTypes.shape({}),
};
ProductCard.defaultProps = {
  product: {
    heroUrl: '',
    title: '',
    slug: null,
  },
};

const ProductModalContent = ({
  selectedProduct,
  onCloseClick,
  brand,
  setSelectedImage,
  selectedImage,
  relatedProducts,
  isRelatedProductsLoading,
  brandSlug,
  sendProductBrandMessage,
  selectedMediaId,
}) => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [applications, setApplications] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [requestNames, setRequestNames] = useState([]);
  const [modalDataLoaded, setModalDataLoaded] = useState(false);
  const [modalDataLoading, setModalDataLoading] = useState(false);

  const loadGalleryImages = () => {
    setModalDataLoading(true);
    ProductResource.getModalData(selectedProduct.djangoId)
      .then((productModalData) => {
        setGalleryImages(productModalData.mediaItems);
        if (selectedMediaId) {
          const image = galleryImages.find(({ djangoId }) => String(djangoId) === selectedMediaId);
          if (image) {
            setSelectedImage(image);
          }
        }

        setCategories(productModalData.categories);

        setMaterials(productModalData.tagsMaterials);
        setApplications(productModalData.tagsApplications);
        setPerformance(productModalData.tagsPerformance);
        setRequestNames(productModalData.tagsRequestNames);

        setModalDataLoading(false);
        setModalDataLoaded(true);
      })
      .catch((err) => {
        setModalDataLoading(false);
        setModalDataLoaded(true);
        Raven.captureException(err);
      });
  };

  useEffect(() => {
    if (!modalDataLoading && !modalDataLoaded) {
      loadGalleryImages();
    }
  });

  const breadcrumb = selectedProduct.breadcrumb && {
    text: selectedProduct.breadcrumb[0],
    slug: selectedProduct.breadcrumb[1],
  };

  const imageInfo = (
    <>
      {selectedProduct.description
        ? (
          <div className="row">
            <div className="shrink columns mb-base">
              <p className="meta mb-xxxs">Description</p>
              <div style={{ whiteSpace: 'pre-wrap' }}>
                <ReadMoreText text={selectedProduct.description} />
              </div>
            </div>
          </div>
        ) : null}
      {materials && materials.length > 0 ? <TagsDisplay tags={materials.map(tag => tag.text)} tagsGroupName="Materials" /> : null}
      {applications && applications.length > 0 ? <TagsDisplay tags={applications.map(tag => tag.text)} tagsGroupName="Applications" /> : null}
      {performance && performance.length > 0 ? <TagsDisplay tags={performance.map(tag => tag.text)} tagsGroupName="Performance" /> : null}
      {requestNames && requestNames.length > 0 ? <TagsDisplay tags={requestNames.map(tag => tag.text)} tagsGroupName="Tags" /> : null}
    </>
  );
  const sendMessage = (
    <a
      tabIndex="0"
      role="button"
      className="button white fw-medium mb-xs mt-xs"
      onClick={sendProductBrandMessage}
    >
      <>
        <span className="material-icons fs-s mr-s">chat_bubble</span>
        {' '}
        Send Message
      </>
    </a>
  );

  return (
    <div className="product-modal">
      <div className="header mb-0">
        <div className="row">
          <div className="columns">
            <div className="fs-s mb-s">
              <Fragment key="all-products">
                <a href="/product-search/" className="black">All Products</a>
                {breadcrumb && <i className="material-icons">keyboard_arrow_right</i>}
              </Fragment>

              {breadcrumb && (
              <Fragment key={breadcrumb.slug}>
                <a href={`/product-search/?category=${encodeURIComponent(breadcrumb.slug)}`} className="black">{breadcrumb.text}</a>
              </Fragment>
              )}
            </div>
          </div>
          <div className="ProGlyph shrink">
            <button
              className="close-button light-gray"
              aria-label="Close modal"
              type="button"
              onClick={onCloseClick}
            >
              <span
                className="material-icons"
                aria-hidden="true"
              >
                close
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className="grid-x">
        {/* left hand side */}
        <div className="large-8 medium-6 small-12">
          <div className="row align-middle">
            <div className="columns">
              <div className="pb-m ml-xxxs product-header-mobile">
                <div className="row">
                  <div className="shrink black pr-xxs">
                    <span className="mb-0 fs-l product-name">{selectedProduct.name}</span>
                    <br />
                    <span className="mb-0">
                      by <a href={window.BRAND_PROFILE_URL}>{brand.name}</a>
                    </span>
                  </div>
                </div>
                {selectedProduct.projectsCount > 0
                  ? (
                    <div className="row">
                      <div className="shrink">
                        <span className="material-icons trending-up fs-m">trending_up</span>
                        <span className="ml-xxs fs-s">Used in {selectedProduct.projectsCount} Projects</span>
                      </div>
                    </div>
                  ) : null}
                {categories && categories.length
                  ? (
                    <div className="row">
                      <div className="shrink mb-base">
                        {categories.map((category, i) => (
                          <span key={category.djangoId}>
                            <a href={category.productSearchUrl}>{category.text}</a>
                            {i + 1 < categories.length && <span>,&nbsp;</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                {userResource.canContactBrand()
                  && (
                  <div className="row">
                    <div className="shrink">
                      {sendMessage}
                    </div>
                  </div>
                  )}
              </div>
              <div className="row">
                <div className="columns">
                  {!modalDataLoading && modalDataLoaded && galleryImages.length > 0
                    ? (
                      <ModalGallery
                        images={galleryImages}
                        setSelectedImage={setSelectedImage}
                        selectedImage={selectedImage}
                      />
                    )
                    : <img src={defaultProductImageUrl} style={{ width: '100%' }} alt={selectedProduct.name} />}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* right hand side */}
        <div className="large-4 medium-6 small-12">
          <div className="row align-middle">
            <div className="columns">
              <div className="row ml-xxxs product-header-desktop">
                <div className="shrink black pr-xxs">
                  <span className="mb-0 fs-l product-name">{selectedProduct.name}</span>
                  <br />
                  <div>
                    by <a href={window.BRAND_PROFILE_URL}>{brand.name}</a>
                  </div>
                </div>
                {selectedProduct.projectsCount > 0
                  ? (
                    <div className="row">
                      <div className="shrink columns">
                        <span className="material-icons trending-up fs-m">trending_up</span>
                        <span className="ml-xxs fs-s">Used in {selectedProduct.projectsCount} Projects</span>
                      </div>
                    </div>
                  ) : null}
                {categories && categories.length
                  ? (
                    <div className="row">
                      <div className="shrink columns">
                        {categories.map((category, i) => (
                          <span key={category.djangoId}>
                            <a href={category.productSearchUrl}>{category.text}</a>
                            {i + 1 < categories.length && <span>,&nbsp;</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                {userResource.canContactBrand()
                  && (
                  <div className="row">
                    <div className="columns shrink">
                      {sendMessage}
                    </div>
                  </div>
                  )}
              </div>
            </div>
          </div>
          {imageInfo}
        </div>
      </div>
      <div className="row">
        <div className="columns pr-0">
          <h4 className="mt-xl mb-base">
            More from {brand.name}
          </h4>
          {isRelatedProductsLoading
            ? <div className="loading-spinner-m" style={{ margin: 'auto' }} />
            : <></>}
          <div className="row small-up-2 medium-up-2 large-up-3">
            {relatedProducts.map(product => (
              <ProductCard key={product.slug} product={product} brandSlug={brandSlug} />
            ))}
          </div>
          {!relatedProducts && !isRelatedProductsLoading
          && <div className="text-center"><p>No Products Found</p></div>}
        </div>
      </div>
    </div>
  );
};

ProductModalContent.propTypes = {
  selectedProduct: PropTypes.shape({
  }).isRequired,
  onCloseClick: PropTypes.func.isRequired,
  brand: PropTypes.shape({}).isRequired,
  contact: PropTypes.shape({}).isRequired,
  validation: PropTypes.shape({}).isRequired,
  setSelectedImage: PropTypes.func.isRequired,
  selectedImage: PropTypes.shape({}),
  relatedProducts: PropTypes.arrayOf(PropTypes.shape({})),
  isRelatedProductsLoading: PropTypes.bool,
  brandSlug: PropTypes.string.isRequired,
  sendProductBrandMessage: PropTypes.func,
};

ProductModalContent.defaultProps = {
  selectedImage: {},
  relatedProducts: [],
  isRelatedProductsLoading: false,
  sendProductBrandMessage: () => {},
};

export default class BrandDetailProducts extends Component {
  constructor(props) {
    super(props);
    this.productModal = null;
    this.state = this.defaultState();
    this.state.slug = BRAND_SLUG;
    this.state.baseUrl = `/brands/${BRAND_SLUG}/products/`;
    this.state.selectedProductSlug = this.props.match.params.selectedProductSlug;
    this.state.selectedMediaId = this.props.match.params.selectedMediaId;
    this.state.brand = {
      logoUrl: '',
      djangoId: '',
      name: '',
      avatars: [],
      numRepresentatives: 0,
    };
    this.state.contact = {
      name: '',
      email: '',
      requestType: '',
      message: '',
    };
    this.state.validation = {
      required: {
        name: true,
        email: true,
        requestType: true,
        message: true,
      },
      formatting: {
        email: true,
      },
    };
    this.state.failure = false;
    this.state.sending = false;
    this.state.messageSent = false;
  }

  UNSAFE_componentWillMount() {
    // Unpack props from Django
    const props = iterUnpack(JSON.parse(window.props));
    // Set 'hero' images for all products if possible
    const brandProps = props.allBrands[0];
    brandProps.products = this.setFakeHeros(brandProps.products);
    const {
      people,
      logo,
      djangoId,
      name,
    } = props.allBrands[0];

    /* TODO user `mediatype` param on this field in GraphQL instead of this: */
    const avatars = people.reduce((accumulator, { id, user }) => {
      const mediaArray = user.avatar;
      const logoObj = mediaArray[0];
      const url = logoObj ? logoObj.mediaItem.thumbnail : defaultUserAvatarUrl;
      accumulator.push({
        id,
        url,
      });
      return accumulator;
    }, []);
    const brandLogoUrl = logo.length ? logo[0].mediaItem.thumbnail : defaultLogoImageUrl;
    const brandState = Object.assign({}, this.state.brand, {
      avatars: avatars.slice(0, 6),
      numRepresentatives: avatars.length,
      logoUrl: brandLogoUrl,
      djangoId,
      name,
    });
    const levelOneCategoriesFacet = [{
      name: 'All',
      count: brandProps.productsCount,
    }, ...brandProps.levelOneCategoriesFacet];
    // Set products list data
    this.setState({
      isLoading: false,
      isFiltering: false,
      metadata: props.metadata,
      ...brandProps,
      brand: brandState,
      contact: this.state.contact,
      levelOneCategoriesFacet,
    });
  }

  componentDidMount() {
    // If selectedProduct is present, set the modal to be open
    const { selectedProductSlug } = this.state;
    const { selectedMediaId } = this.state;
    if (selectedProductSlug) {
      try {
        this.openModal(selectedProductSlug, {
          selectedMediaId,
          setHistory: false,
        });
      } catch (modalError) {
        if (modalError.message === 'Product not on state.products') {
          this.fetchProduct()
            .then(product => this.openModal(product.slug, {
              selectedMediaId,
              setHistory: false,
            }))
            .then(() => {
              // Remove the fetched product so it doesn't show in the list
              const { products } = this.state;
              products.pop();
              this.setState({ products });
            })
            .catch((apiError) => {
              if (apiError.message === 'Product not found') {
                this.props.history.replace(this.state.baseUrl);
              } else {
                Raven.captureException(apiError);
              }
            });
        }
      }
    }
    this.props.history.listen(architizerHistoryListener((
      location,
      action,
    ) => {
      // If using back/forward button, don't PUSH state, simply react to state
      /* eslint-disable no-prototype-builtins */
      if (action === 'POP') {
        let state = {};
        if (location.state) state = location.state;
        if (state.hasOwnProperty('slug') && state.slug) {
          this.openModal(state.slug, { selectedMediaId: state.mediaId, setHistory: false });
        } else if (this.state.modalIsOpen) {
          this.closeModal(false);
        }
      }
      /* eslint-enable */
    }));

    userResource.me()
      .then(user => this.setState({ user: user.data }))
      .catch(() => {});
  }

  onProductTypeChange = (filter) => {
    segment.track(ANALYTICS_NAME.BRAND_PRODUCT_TYPE_FILTER_CHANGED);
    this.setState({
      levelOneCategoriesFacet: this.state.levelOneCategoriesFacet.map((e) => {
        if (e.name === filter.name) {
          e.isLoading = true;
        }
        return e;
      }),
      isFiltering: true,
    });
    this.fetchProductsList(filter.name, 'product-type-change');
  }

  setFakeHeros = products => products.map((_product) => {
    const product = _product;
    if ((!product.hero || !product.hero.length) && (product.gallery && product.gallery.length)) {
      product.hero = [product.gallery[0]];
    }
    return product;
  });

  setSelectedImage = (image, setHistory = true) => {
    this.setState({
      selectedImage: image,
    }, () => {
      if (setHistory) {
        this.props.history.push(
          `${this.state.baseUrl}${this.state.selectedProductSlug}/${this.state.selectedImage.djangoId}/`,
          {
            slug: this.state.selectedProductSlug,
            mediaId: this.state.selectedImage.djangoId,
            previousPage: { title: document.title, url: window.location.href },
          },
        );
        segment.page();
        ga4.page();
      }
    });
  }

  getRelatedProducts = () => {
    const { selectedProduct, slug: brandSlug } = this.state;

    const query = `{
      allProducts(first: 1, slug: "${selectedProduct.slug}") {
        edges{
          node{
            similarProductsForBrand(first: 9, brandSlug: "${brandSlug}"){
              edges{
                node{
                  title
                  slug
                  heroUrl
                }
              }
            }
          }
        }
      }
    }`;

    APIClient(query, true)
      .then(data => data.allProducts)
      .then(allProducts => this.setState({
        relatedProducts: allProducts.length ? allProducts[0].similarProductsForBrand : [],
        isRelatedProductsLoading: false,
      }));
  }

  fetchProductsList = (selectedFilter, action = 'product-type-change') => {
    let productsFilterQuery = '';
    if (selectedFilter !== 'All') {
      productsFilterQuery = `category: "${selectedFilter}",`;
    }
    let afterCursor = '';
    if (action === 'show-more') {
      afterCursor = `after: "${this.state.metadata.products.endCursor}",`;
    }
    let numberOfProducts = '18';
    if (action === 'show-more') {
      numberOfProducts = '100';
    }
    const query = `{
      allBrands(slug: "${this.state.slug}") {
        edges {
          node {
            productsCount
            products: filteredProducts(first: ${numberOfProducts}, ${productsFilterQuery} ${afterCursor} orderBy: "-sort_order_i") {
              edges {
                cursor
                ...product
              }
              pageInfo {
                endCursor
                hasNextPage
              }
            }
          }
        }
      }
    }

    fragment mediaItem on MediaItemAttributionNodeConnection {
      edges {
        node {
          type
          mediaItem {
            id
            djangoId
            url
            originalName
          }
        }
      }
    }

    fragment product on ProductNodeEdge {
      node {
        name
        id
        djangoId
        slug
        description
        created
        projectsCount
        breadcrumb
        hero: media(last:1, type:"hero") {
          ...mediaItem
        }
        gallery: media(type:"gallery", distinct:true) {
          ...mediaItem
        }
      }
    }
    `;

    if (action === 'product-type-change' || (action === 'show-more' && this.state.metadata.products.hasNextPage)) {
      APIClient(query, true)
        .then((data) => {
          const newProducts = this.setFakeHeros(data.allBrands[0].products);
          if (action === 'show-more') {
            const products = Array.from(new Set([...this.state.products, ...newProducts]));

            this.setState({
              products,
              metadata: data.metadata,
              isLoading: false,
            });
          } else if (action === 'product-type-change') {
            const { levelOneCategoriesFacet, productsCount } = this.state;
            // eslint-disable-next-line max-len
            const filteredProductsCount = levelOneCategoriesFacet.find(e => e.name === selectedFilter).count || productsCount;

            this.setState({
              metadata: data.metadata,
              products: newProducts,
              selectedProductType: selectedFilter,
              productsCount: filteredProductsCount,
              levelOneCategoriesFacet: levelOneCategoriesFacet.map((e) => {
                e.isLoading = false;
                return e;
              }),
              isFiltering: false,
              isLoading: false,
            });
          }
        });
    }
  }

  openModal = (selectedProductSlug, { selectedMediaId = '', setHistory = true } = {}) => {
    const selectedProduct = this.state.products.find(({ slug }) => slug === selectedProductSlug);
    if (!selectedProduct) {
      throw new Error('Product not on state.products');
    }

    const contactState = Object.assign({}, this.state.contact, {
      subject: `RE: ${selectedProduct.name}`,
    });
    this.setState({
      modalIsOpen: true,
      selectedProduct,
      selectedProductSlug,
      selectedMediaId,
      contact: contactState,
      isRelatedProductsLoading: true,
    }, () => this.getRelatedProducts());
    if (setHistory) {
      this.props.history.push(`${this.state.baseUrl}${selectedProductSlug}/${selectedMediaId}`,
        {
          slug: selectedProductSlug,
          mediaId: selectedMediaId,
          previousPage: { title: document.title, url: window.location.href },
        });
      segment.page();
      ga4.page();
    }
  }

  closeModal = (setHistory = true) => {
    this.setState({
      modalIsOpen: false,
      selectedProduct: this.defaultState().selectedProduct,
      selectedProductSlug: this.defaultState().selectedProductSlug,
      selectedMediaId: this.defaultState().selectedMediaId,
      messageSent: this.defaultState().messageSent,
      selectedImage: this.defaultState().selectedImage,
    });
    if (setHistory) {
      this.props.history.push(this.state.baseUrl, {
        slug: '',
        previousPage: { title: document.title, url: window.location.href },
      });
    }
    this.resetFormState();
  }

  handleMessageSentSuccessfully = () => {
    this.setState({
      messageSent: true,
    });
  }

  resetContactState() {
    return this.setState({
      contact: {
        name: '',
        email: '',
        requestType: '',
        message: '',
      },
      messageSent: false,
    });
  }

  resetValidationState() {
    return this.setState({
      validation: {
        required: {
          name: true,
          email: true,
          requestType: true,
          message: true,
        },
        formatting: {
          email: true, // this comes from backend
        },
      },
    });
  }

  resetFormState() {
    this.resetContactState();
    this.resetValidationState();
  }

  fetchProduct = () => {
    this.setState({ isLoading: true });
    const query = `{
        allProducts(slug: "${this.state.selectedProductSlug}") {
          edges {
            ...product
          }
        }
      }

      fragment mediaItem on MediaItemAttributionNodeConnection {
        edges {
          node {
            type
            mediaItem {
              id
              djangoId
              url
              originalName
            }
          }
        }
      }

      fragment product on ProductNodeEdge {
        node {
          name
          id
          djangoId
          slug
          description
          created
          breadcrumb
          projectsCount
          hero: media(last:1, type:"hero") {
            ...mediaItem
          }
          brands(first:1) {
            edges {
              node {
                brand {
                  slug
                }
              }
            }
          }
        }
      }
      `;

    return APIClient(query, true)
      .then((data) => {
        // Ideally, the fetched product is for this brand...
        let fetchedProductBrandSlug = this.state.slug;
        try {
          // ... if not, catch it here
          fetchedProductBrandSlug = data.allProducts[0].brands[0].brand.slug;
        } catch (e) {
          Raven.captureException('Product doesn\'t have brand attribution', e);
        }
        // If no product is found, or the fetched product is not for this brand...
        if (!data.allProducts.length || fetchedProductBrandSlug !== this.state.slug) {
          throw new Error('Product not found');
        }
        const newProduct = this.setFakeHeros([data.allProducts[0]]);
        const products = Array.from(new Set([...this.state.products, ...newProduct]));
        // Set state with fetched data, checking for emptiness on data.allBrands
        this.setState({
          isLoading: false,
          products,
        });
        return newProduct[0];
      });
  };

  showMoreProductsHandler = () => {
    segment.track(ANALYTICS_NAME.BRAND_SHOW_MORE_PRODUCTS_CLICKED);
    this.setState({ isLoading: true });
    this.fetchProductsList(this.state.selectedProductType, 'show-more');
  }

  sendProductBrandMessage = (e) => {
    e.preventDefault();
    const contact = () => window.openBrandMessagePopup({
      productId: this.state.selectedProduct.djangoId,
      productName: this.state.selectedProduct.name,
    });

    // Generic and Non Pro users don't have Intercom accounts per Intercom limitations,
    // thus, identify them on demand to create accounts for them
    if (userResource.isGeneric() || userResource.isNonProfessional()) {
      segment.identify({ force: true, callback: contact });
    } else {
      contact();
    }
  }

  defaultState = () => ({
    isLoading: true,
    isFiltering: false,
    products: [],
    levelOneCategoriesFacet: [],
    selectedProductType: 'All',
    isBrandAdmin: IS_BRAND_ADMIN,
    selectedProduct: {
      id: '',
      djangoId: '',
      hero: [],
      gallery: [],
      description: '',
      materials: [],
      applications: [],
      performance: [],
      requestNames: [],
      created: '',
    },
    metadata: {
      products: {
        hasNextPage: true,
      },
    },
    slug: '',
    modalIsOpen: false,
    selectedProductSlug: '',
    selectedMediaId: '',
    selectedImage: {},
    brand: {},
    contact: {},
    validation: {},
    failure: false,
    sending: false,
    messageSent: false,
    relatedProducts: [],
    isRelatedProductsLoading: false,
    user: null,
    fallbackContactForm: false,
  });

  render() {
    const {
      products,
      levelOneCategoriesFacet,
      productsCount,
      selectedProduct,
      isLoading,
      isFiltering,
    } = this.state;

    return (
      <div className="row">
        <h3 className="columns small-12 pb-base fs-m fw-medium">
          <i className="material-icons">local_offer</i>
          {' '}
          Products
        </h3>
        {products && products.length
          ? (
            <div className="columns small-12">
              <BrandViewProductsContent
                products={products}
                levelOneCategoriesFacet={levelOneCategoriesFacet}
                selectedProductType={this.state.selectedProductType}
                onProductTypeChange={this.onProductTypeChange}
                productsCount={productsCount}
                showMoreProductsHandler={this.showMoreProductsHandler}
                onProductOpenClick={this.openModal}
                hasNextPage={this.state.metadata.products.hasNextPage}
                selectedProduct={selectedProduct}
                isLoading={isLoading}
                isFiltering={isFiltering}
              />
              <Modal
                isOpen={this.state.modalIsOpen}
                onRequestClose={this.closeModal}
                className="revealProductMessage"
                overlayClassName="reveal-overlay popup-modal-overlay"
                shouldCloseOnOverlayClick
              >
                <ProductModalContent
                  selectedProduct={selectedProduct}
                  onCloseClick={this.closeModal}
                  brand={this.state.brand}
                  contact={this.state.contact}
                  validation={this.state.validation}
                  sending={this.state.sending}
                  messageSent={this.state.messageSent}
                  failure={this.state.failure}
                  onMessageSentSuccessfully={this.handleMessageSentSuccessfully}
                  setSelectedImage={this.setSelectedImage}
                  selectedImage={this.state.selectedImage}
                  relatedProducts={this.state.relatedProducts}
                  isRelatedProductsLoading={this.state.isRelatedProductsLoading}
                  brandSlug={this.state.slug}
                  sendProductBrandMessage={this.sendProductBrandMessage}
                  fallbackForm={this.state.fallbackContactForm}
                  selectedMediaId={this.state.selectedMediaId}
                />
              </Modal>
            </div>
          )
          : (
            <Alert type="secondary" className="columns small-12">
              {isLoading
                ? <div className="loading-spinner-m" style={{ margin: 'auto' }} />
                : <div>No Products Available</div>}
            </Alert>
          )}
        {!this.props.hideDivider
        && (
        <div className="columns small-12">
          <hr />
        </div>
        )}
      </div>
    );
  }
}

BrandDetailProducts.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      selectedProductSlug: PropTypes.string,
      selectedMediaId: PropTypes.string,
    }),
  }).isRequired,
  history: PropTypes.shape({
    location: PropTypes.shape({
      state: PropTypes.shape({}),
    }).isRequired,
    listen: PropTypes.func,
    push: PropTypes.func,
    replace: PropTypes.func,
  }).isRequired,
  hideDivider: PropTypes.bool,
};

BrandDetailProducts.defaultProps = {
  hideDivider: false,
};
